import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { typedCollection, getCollection } from '@/lib/firebase/firestore'
import type { ServiceDocument } from '@/types'
import { boardTemplatesService } from '../services/boardTemplates.service'

type Props = {
  open: boolean
  boardId: string
  defaultName?: string
  onClose: () => void
}

type Service = ServiceDocument & { id: string }

export function SaveAsTemplateDialog({ open, boardId, defaultName, onClose }: Props) {
  const [name, setName] = useState(defaultName ?? '')
  const [description, setDescription] = useState('')
  const [defaultForServiceIds, setDefaultForServiceIds] = useState<string[]>([])
  const [services, setServices] = useState<Service[]>([])
  const [servicesError, setServicesError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Reset form state whenever the dialog opens
  useEffect(() => {
    if (!open) return
    setName(defaultName ?? '')
    setDescription('')
    setDefaultForServiceIds([])
  }, [open, defaultName])

  // Load services when opened
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setServicesError(null)
    void (async () => {
      try {
        const result = await getCollection(typedCollection<ServiceDocument>('services'))
        if (!cancelled) setServices(result)
      } catch (e) {
        if (!cancelled) setServicesError(formatUserFacingError(e, 'We could not complete that action. Please try again.'))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open])

  function toggleService(id: string) {
    setDefaultForServiceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmedName = name.trim()
    if (!trimmedName) {
      alert(withErrorCode('Name is required.', 'app/validation'))
      return
    }
    setSubmitting(true)
    try {
      const trimmedDesc = description.trim()
      await boardTemplatesService.saveBoardAsTemplate({
        boardId,
        name: trimmedName,
        ...(trimmedDesc ? { description: trimmedDesc } : {}),
        defaultForServiceIds,
      })
      alert('Template saved.')
      onClose()
    } catch (err) {
      alert(formatUserFacingError(err, 'We could not complete that action. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Save board as template</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sat-name">Name</Label>
            <Input
              id="sat-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={submitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sat-desc">Description</Label>
            <Textarea
              id="sat-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="space-y-2">
            <Label>Default for services</Label>
            {servicesError && (
              <p className="text-xs text-destructive">{servicesError}</p>
            )}
            <div className="max-h-56 overflow-y-auto rounded-md border p-2 space-y-1">
              {services.length === 0 ? (
                <p className="text-xs text-muted-foreground p-2">
                  {servicesError
                    ? withErrorCode(
                        'We could not load services. Please try again.',
                        'project-board-templates/services-load',
                      )
                    : 'No services found.'}
                </p>
              ) : (
                services.map((s) => {
                  const checked = defaultForServiceIds.includes(s.id)
                  return (
                    <label
                      key={s.id}
                      className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted cursor-pointer text-sm"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleService(s.id)}
                        disabled={submitting}
                      />
                      <span>{s.title}</span>
                    </label>
                  )
                })
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save template'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default SaveAsTemplateDialog
